import { loadProgress, saveProgress, getSelectedHeroId, setSelectedHeroId } from '../shared/progress';
import { isHeroUnlocked } from '../game/progression/commander';
import { FACTIONS } from '../shared/factions';
import { SKILL_CATALOG } from '../shared/skills';
import { getSkillLevel } from '../shared/skill-level';
import type { FactionId, PlayerProgress } from '../shared/types';

/**
 * 主人翁 / 英雄選擇畫面 (主人翁系統 v1, docs/spec/HERO_SYSTEM_V1.md §1.2).
 *
 * 學習與遊戲分離鐵則: 這是純遊戲端的「選人」畫面 — 選英雄 = 選門派玩法 +
 * 戰車外觀 + 立繪, 絕對不彈題目。寫入 `progress.selectedHeroId`, 回 home。
 * 可重選 (再進來會 highlight 目前選擇)。
 *
 * 雙軌進度鐵則 (PROGRESSION_AND_ACCOUNTS.md §3.2.3): 未解鎖英雄顯示鎖頭、
 * 不可選。解鎖讀玩遊戲軌 `progress.unlockedHeroes`(由 commanderLevel 解鎖,
 * celestial 預設已解鎖)。鎖頭只是「廣度」門檻,**絕不**因此彈題目或碰學習軌。
 *
 * 美術 (立繪 `public/assets/hero/{faction}-portrait.png`) 還沒到時, 程式
 * 嘗試載入, 失敗自動 fallback 到門派純色塊 + emoji, 不可白畫面。
 */

export interface HeroSelectOptions {
  /** 確認選擇後呼叫 (通常 → home)。傳入剛選的英雄 id。 */
  onConfirm: (heroId: FactionId) => void;
  /** 返回不選 (可選)。 */
  onBack?: () => void;
}

/** 一句話玩法 (spec §1.2) — 給卡片的副標。 */
export interface HeroCardData {
  id: FactionId;
  /** 英雄名 (區別於門派名 displayName)。 */
  heroName: string;
  /** 玩法一句話。 */
  tagline: string;
}

/** spec §1.2 的 5 英雄資料 (玩法一句話)。輸出供測試斷言 5 卡。 */
export const HERO_CARDS: readonly HeroCardData[] = [
  { id: 'celestial', heroName: '星算俠', tagline: '爆發連射,精準秒殺' },
  { id: 'runic',     heroName: '紫苑', tagline: '展開符文盾,吃傷活久' },
  { id: 'techno',    heroName: '機械斥', tagline: '偵察提速,高速壓制' },
  { id: 'elemental', heroName: '自然御', tagline: '火柱持續,鋪場燃燒' },
  { id: 'dominion',  heroName: '愛紗', tagline: '範圍轟炸,一波清場' },
];

/** 0xRRGGBB → '#rrggbb' (FACTIONS.colorPrimary 是 number)。 */
function hexColor(n: number): string {
  return '#' + n.toString(16).padStart(6, '0');
}

/** 立繪檔路徑 (Track A 美術產出處)。載入失敗 fallback 色塊。 */
export function heroPortraitSrc(id: FactionId): string {
  return `/assets/hero/${id}-portrait.png`;
}

/**
 * 戰車縮圖路徑 — 必須跟「戰鬥中實際載入的玩家戰車」同一份來源檔,
 * 這樣「選擇主角」看到的縮圖 = 戰鬥裡開的那台。
 *
 * 戰鬥端 (HorizontalBattleScene) 載入 `assets/sprites/tank-player-{faction}.png`
 * (見 src/game/entities/sprite-keys.ts PLAYER_TANK_SPRITE_URLS),所以這裡指向
 * 同一個檔。先前縮圖讀的是 `/assets/ai-gen/tanks/...`,跟戰鬥不同步,已修正。
 */
export function heroTankSrc(id: FactionId): string {
  return `/assets/sprites/tank-player-${id}.png`;
}

// ── 雙軌徽章資料讀取 (CHARACTER_SYSTEM_DESIGN.md §3.6 / §6.4) ──────────────
// 純讀 PlayerProgress,絕不寫任何欄位。讓孩子在選角卡同時看到:
//   學習軌 = 變強 (該門派技能等級,讀 skillLevels)
//   玩遊戲軌 = 變多花樣 (已解鎖英雄數,讀 unlockedHeroes;
//              unlockedAccessories 型別尚不存在 → 暫以 unlockedHeroes 計數)。

/**
 * 學習軌徽章值:該英雄(門派)目前最高的技能等級 (讀 skillLevels)。
 * 一個門派有多個技能 (lv1/lv2/lv3 槽),取其中最高 level 當「英雄技能 Lv」。
 * 0 = 該門派尚無任何技能升級 (徽章不顯示)。純讀,不寫。
 */
export function heroSkillLevel(progress: PlayerProgress | null, faction: FactionId): number {
  if (!progress) return 0;
  let max = 0;
  for (const skill of SKILL_CATALOG) {
    if (skill.faction !== faction) continue;
    const lv = getSkillLevel(progress, skill.id);
    if (lv > max) max = lv;
  }
  return max;
}

/**
 * 玩遊戲軌徽章值:已解鎖英雄數 (讀 unlockedHeroes)。
 * unlockedHeroes 不存在(舊存檔尚未 migrate)→ 0 (徽章不顯示)。純讀,不寫。
 * NOTE: 規格 §5.2 規劃的 unlockedAccessories 型別尚未建立,故玩遊戲軌
 *       先以「已解鎖英雄數」呈現「廣度」;待配件系統落地再切換來源。
 */
export function heroUnlockedBreadth(progress: PlayerProgress | null): number {
  if (!progress) return 0;
  const heroes = progress.unlockedHeroes;
  if (!Array.isArray(heroes)) return 0;
  return heroes.length;
}

export function renderHeroSelect(container: HTMLElement, options: HeroSelectOptions): void {
  const progress = loadProgress();
  const current: FactionId | null = progress ? getSelectedHeroId(progress) : null;

  const root = document.createElement('div');
  root.className = 'hero-select';
  root.setAttribute('data-testid', 'hero-select');

  const header = document.createElement('div');
  header.className = 'hero-select-header';
  header.innerHTML = `
    <h1 style="margin:0 0 4px;">選擇主角</h1>
    <p class="hero-select-sub" style="margin:0;opacity:0.8;">選一位英雄 — 決定你的戰車與戰鬥玩法</p>
  `;
  root.appendChild(header);

  const grid = document.createElement('div');
  grid.className = 'hero-card-grid';
  grid.setAttribute('data-testid', 'hero-card-grid');
  grid.style.cssText = 'display:flex;flex-wrap:wrap;gap:14px;justify-content:center;margin-top:16px;';
  root.appendChild(grid);

  for (const card of HERO_CARDS) {
    grid.appendChild(buildHeroCard(card, current, options, progress));
  }

  if (options.onBack) {
    const back = document.createElement('button');
    back.className = 'secondary-btn hero-select-back';
    back.textContent = '← 返回';
    back.style.cssText = 'margin-top:18px;';
    back.addEventListener('click', () => options.onBack!());
    root.appendChild(back);
  }

  container.appendChild(root);
}

function buildHeroCard(
  card: HeroCardData,
  current: FactionId | null,
  options: HeroSelectOptions,
  progress: PlayerProgress | null,
): HTMLElement {
  const faction = FACTIONS[card.id];
  const color = hexColor(faction.colorPrimary);
  const selected = current === card.id;
  // 雙軌進度:未解鎖英雄上鎖頭、不可選。無 progress(理論不會)→ 視為已解鎖
  // 以免白卡。celestial(starter)永遠解鎖。
  const unlocked = progress ? isHeroUnlocked(progress, card.id) : true;
  const locked = !unlocked;

  const el = document.createElement('button');
  el.className = 'hero-card'
    + (selected ? ' hero-card-selected' : '')
    + (locked ? ' hero-card-locked' : '');
  el.setAttribute('data-testid', `hero-card-${card.id}`);
  el.setAttribute('data-hero-id', card.id);
  if (locked) {
    el.setAttribute('data-locked', 'true');
    el.disabled = true;
    el.setAttribute('aria-disabled', 'true');
  }
  el.style.cssText = [
    'display:flex',
    'flex-direction:column',
    'align-items:center',
    'width:160px',
    'padding:12px',
    'border-radius:16px',
    locked ? 'cursor:not-allowed' : 'cursor:pointer',
    'background:#1e1e2a',
    `border:3px solid ${selected ? color : 'transparent'}`,
    'box-shadow:0 4px 12px rgba(0,0,0,0.3)',
    'color:#fff',
    'text-align:center',
    'position:relative',
    locked ? 'opacity:0.55' : 'opacity:1',
  ].join(';');

  // ── 立繪 / 色塊 placeholder 區 ──
  // 嘗試載入 portrait png; 失敗 (圖還沒到) → 顯示門派色塊 + emoji。
  const portrait = document.createElement('div');
  portrait.className = 'hero-portrait';
  portrait.style.cssText = [
    'width:120px',
    'height:120px',
    'border-radius:12px',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'overflow:hidden',
    'position:relative',
    `background:${color}`,
  ].join(';');

  // 色塊 fallback 內容 (emoji) — 預設先顯示, 圖載入成功才蓋掉。
  const fallback = document.createElement('div');
  fallback.className = 'hero-portrait-fallback';
  fallback.setAttribute('data-testid', `hero-portrait-fallback-${card.id}`);
  fallback.textContent = faction.emoji;
  fallback.style.cssText = 'font-size:56px;line-height:1;';
  portrait.appendChild(fallback);

  const img = document.createElement('img');
  img.className = 'hero-portrait-img';
  img.alt = card.heroName;
  img.style.cssText = 'width:100%;height:100%;object-fit:cover;display:none;position:absolute;inset:0;';
  // 載入成功 → 顯示圖、隱藏色塊;失敗 → 維持色塊 (不可白畫面)。
  img.addEventListener('load', () => {
    img.style.display = 'block';
    fallback.style.display = 'none';
  });
  img.addEventListener('error', () => {
    img.style.display = 'none';
    fallback.style.display = 'flex';
  });
  img.src = heroPortraitSrc(card.id);
  portrait.appendChild(img);

  el.appendChild(portrait);

  // ── 英雄名 + 門派 + 玩法一句話 + 戰車縮圖區 ──
  const name = document.createElement('div');
  name.className = 'hero-card-name';
  name.style.cssText = 'font-size:18px;font-weight:700;margin-top:10px;';
  name.textContent = `${faction.emoji} ${card.heroName}`;
  el.appendChild(name);

  const facName = document.createElement('div');
  facName.className = 'hero-card-faction';
  facName.style.cssText = `font-size:12px;margin-top:2px;color:${color};`;
  facName.textContent = faction.displayName;
  el.appendChild(facName);

  const tag = document.createElement('div');
  tag.className = 'hero-card-tagline';
  tag.style.cssText = 'font-size:13px;margin-top:6px;opacity:0.85;min-height:34px;';
  tag.textContent = card.tagline;
  el.appendChild(tag);

  // ── 雙軌徽章 (§3.6 / §6.4):學習=變強、玩遊戲=變多花樣。純展示,不寫欄位。──
  // 兩軌視覺分流(顏色/圖示),欄位無值則該徽章不顯示 (fallback 不硬寫)。
  const badges = document.createElement('div');
  badges.className = 'hero-card-tracks';
  badges.setAttribute('data-testid', `hero-card-tracks-${card.id}`);

  // 學習軌:該門派技能等級 (讀 skillLevels)。Lv0 = 尚未升級,不顯示。
  const skillLv = heroSkillLevel(progress, card.id);
  if (skillLv >= 1) {
    const learn = document.createElement('span');
    learn.className = 'hero-track hero-track-learn';
    learn.setAttribute('data-testid', `hero-track-learn-${card.id}`);
    learn.setAttribute('data-skill-level', String(skillLv));
    learn.textContent = `📘 技能 Lv${skillLv}`;
    learn.title = '學習讓這位英雄更強(技能等級)';
    badges.appendChild(learn);
  }

  // 玩遊戲軌:已解鎖英雄數 (讀 unlockedHeroes)。0 = 無資料,不顯示。
  const breadth = heroUnlockedBreadth(progress);
  if (breadth >= 1) {
    const play = document.createElement('span');
    play.className = 'hero-track hero-track-play';
    play.setAttribute('data-testid', `hero-track-play-${card.id}`);
    play.setAttribute('data-unlocked-count', String(breadth));
    play.textContent = `🎮 已解鎖 ${breadth} 英雄`;
    play.title = '玩遊戲讓你解鎖更多英雄/花樣';
    badges.appendChild(play);
  }

  if (badges.childElementCount > 0) {
    el.appendChild(badges);
  }

  // 戰車區 — 顯示該英雄的真實戰車 sprite (坦克世界卡通風)。載入失敗 fallback 🚜 色條。
  const tankSlot = document.createElement('div');
  tankSlot.className = 'hero-card-tank';
  tankSlot.setAttribute('data-testid', `hero-card-tank-${card.id}`);
  tankSlot.style.cssText = [
    'margin-top:8px',
    'width:90%',
    'min-height:64px',
    'border-radius:8px',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'overflow:hidden',
    `background:${color}1a`,
    `border:1px solid ${color}55`,
  ].join(';');
  const tankFallback = document.createElement('span');
  tankFallback.className = 'hero-card-tank-fallback';
  tankFallback.style.cssText = 'font-size:11px;opacity:0.9;';
  tankFallback.textContent = '🚜 戰車';
  tankSlot.appendChild(tankFallback);
  const tankImg = document.createElement('img');
  tankImg.className = 'hero-card-tank-img';
  tankImg.alt = `${card.heroName}的戰車`;
  tankImg.style.cssText = 'max-width:100%;max-height:80px;object-fit:contain;display:none;';
  tankImg.addEventListener('load', () => { tankImg.style.display = 'block'; tankFallback.style.display = 'none'; });
  tankImg.addEventListener('error', () => { tankImg.style.display = 'none'; tankFallback.style.display = 'inline'; });
  tankImg.src = heroTankSrc(card.id);
  tankSlot.appendChild(tankImg);
  el.appendChild(tankSlot);

  if (selected && !locked) {
    const badge = document.createElement('div');
    badge.className = 'hero-card-current-badge';
    badge.style.cssText = `margin-top:8px;font-size:12px;font-weight:700;color:${color};`;
    badge.textContent = '✓ 目前選擇';
    el.appendChild(badge);
  }

  // 雙軌進度:鎖頭 overlay(未解鎖)。純廣度門檻,絕不彈題目。
  if (locked) {
    const lock = document.createElement('div');
    lock.className = 'hero-card-lock';
    lock.setAttribute('data-testid', `hero-card-lock-${card.id}`);
    lock.style.cssText = [
      'position:absolute',
      'top:8px',
      'right:8px',
      'font-size:24px',
      'line-height:1',
      'filter:drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
    ].join(';');
    lock.textContent = '🔒';
    el.appendChild(lock);

    const hint = document.createElement('div');
    hint.className = 'hero-card-lock-hint';
    hint.style.cssText = 'margin-top:8px;font-size:12px;font-weight:700;color:#ffb74d;';
    hint.textContent = '提升指揮官等級解鎖';
    el.appendChild(hint);
  }

  el.addEventListener('click', () => {
    // 未解鎖 → 不可選 / 不可確認(disabled 已擋大部分;此處 belt-and-suspenders)。
    if (locked) return;
    selectHero(card.id);
    options.onConfirm(card.id);
  });

  return el;
}

/**
 * 寫入 selectedHeroId 並存檔。抽出來方便測試。
 * 若 progress 不存在 (理論上選人前一定已建檔) — no-op, 交給 onConfirm 流程。
 */
export function selectHero(id: FactionId): void {
  const progress = loadProgress();
  if (!progress) return;
  // 雙軌進度:未解鎖英雄不可選(防呆,UI 已 disable)。
  if (!isHeroUnlocked(progress, id)) return;
  setSelectedHeroId(progress, id);
  saveProgress(progress);
}
